import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dashSc from '../assets/images/dash-sc.png';

const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&display=swap';

const STAR_PATH = "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z";

const Star = ({ fillPercent = 100 }) => {
  // Renders a filled star where `fillPercent` (0-100) of the width is gold and
  // the rest is a muted outline. Used for fractional ratings like 4.6/5.
  if (fillPercent >= 100) {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="#facc15" aria-hidden="true">
        <path d={STAR_PATH} />
      </svg>
    );
  }
  if (fillPercent <= 0) {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="#e5e7eb" aria-hidden="true">
        <path d={STAR_PATH} />
      </svg>
    );
  }
  // Partial fill via a clip-path trick: gold layer clipped to fillPercent width,
  // gray layer underneath.
  return (
    <span style={{ position: 'relative', display: 'inline-block', width: 14, height: 14, lineHeight: 0 }} aria-hidden="true">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="#e5e7eb" style={{ position: 'absolute', inset: 0 }}>
        <path d={STAR_PATH} />
      </svg>
      <svg
        width="14" height="14" viewBox="0 0 24 24" fill="#facc15"
        style={{ position: 'absolute', inset: 0, clipPath: `inset(0 ${100 - fillPercent}% 0 0)` }}
      >
        <path d={STAR_PATH} />
      </svg>
    </span>
  );
};

const renderStars = (rating, outOf = 5) => {
  const stars = [];
  for (let i = 0; i < outOf; i++) {
    const diff = rating - i;
    const pct = diff >= 1 ? 100 : diff <= 0 ? 0 : Math.round(diff * 100);
    stars.push(<Star key={i} fillPercent={pct} />);
  }
  return stars;
};

export default function HomeHero() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_HREF}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = FONT_HREF;
      document.head.appendChild(link);
    }
  }, []);

  return (
    <section className="hh">
      <div className="hh-container">
        <div className="hh-trust hh-anim" style={{ '--d': '0ms' }}>
          <span className="hh-trust-text"><strong>19.4M</strong> messages delivered</span>
          <span className="hh-trust-sep" />
          <span className="hh-trust-text"><strong>3,800+</strong> phones online</span>
          <span className="hh-trust-sep" />
          <span className="hh-trust-text"><strong>99.2%</strong> carrier delivery</span>
        </div>

        <h1 className="hh-headline hh-anim" style={{ '--d': '80ms' }}>
          Skip 10DLC.<br />
          SMS From $0.001.
        </h1>

        <p className="hh-subhead hh-anim" style={{ '--d': '160ms' }}>
          Install our app on a spare Android, then send SMS via dashboard, HighLevel or API.
        </p>

        <div className="hh-benefits hh-anim" style={{ '--d': '200ms' }}>
          
          <span className="hh-benefit-dot" aria-hidden="true" />
          <span className="hh-benefit-chip">
            <span className="hh-benefit-no">No</span>
            <span>per-message markup</span>
          </span>
          <span className="hh-benefit-dot" aria-hidden="true" />
          <span className="hh-benefit-chip">
            <span className="hh-benefit-no">No</span>
            <span>link blocking</span>
          </span>
          <span className="hh-benefit-dot" aria-hidden="true" />
          
        </div>

        <div className="hh-price-row hh-anim" style={{ '--d': '240ms' }}>
          <div className="hh-price">
            <span className="hh-price-old">Twilio <s>$0.0075</s></span>
            <span className="hh-price-arrow">→</span>
            <span className="hh-price-new">
              <span className="hh-price-amt">$0.001</span>
              <span className="hh-price-per">/ SMS</span>
            </span>
          </div>

          <div className="hh-ctas">
            <button className="hh-btn-primary" onClick={() => navigate('/signup')}>
              Start sending free
            </button>
          </div>
        </div>

        <p className="hh-friction hh-anim" style={{ '--d': '340ms' }}>
          No credit card · ~5 min setup
        </p>

        <p className="hh-platforms hh-anim" style={{ '--d': '400ms' }}>
          Works with <a href="/how-textlink-works">Android</a>,{' '}
          <a href="/ghl" className="hh-platforms-ghl">
            <img
              src="https://play-lh.googleusercontent.com/MAbanjWwLE2Ps3c6ZxUgLE6SMSzwy8PQ5mYvc_Txhn_1zv9gLEEKSsLYqW89y1wGdww"
              alt=""
              loading="lazy"
              className="hh-platforms-ghl-logo"
            />
            GoHighLevel
          </a>
          ,{' '}<a href="/smsapi">REST API</a>, <a href="/web-sms-app">Web</a>
        </p>
      </div>

      <div className="hh-product-panel hh-anim hh-anim-panel" style={{ '--d': '500ms' }}>
        <div className="hh-product-frame hh-float">
          <img src={dashSc} alt="TextLink dashboard" className="hh-product-image" />

          {/* Floating benefit cards */}
          <div className="hh-chip hh-chip--tl" style={{ '--d': '900ms' }}>
            <span className="hh-chip-icon hh-chip-icon--green">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            <div className="hh-chip-text">
              <div className="hh-chip-title">No A2P</div>
              <div className="hh-chip-sub">Skip 10DLC entirely</div>
            </div>
          </div>

          <div className="hh-chip hh-chip--ml" style={{ '--d': '1000ms' }}>
            <span className="hh-chip-icon hh-chip-icon--violet">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            </span>
            <div className="hh-chip-text">
              <div className="hh-chip-title">2.3s avg delivery</div>
              <div className="hh-chip-sub">Carrier‑grade routing</div>
            </div>
          </div>

          <div className="hh-chip hh-chip--bl" style={{ '--d': '1100ms' }}>
            <span className="hh-chip-icon hh-chip-icon--savings">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                <polyline points="17 6 23 6 23 12" />
              </svg>
            </span>
            <div className="hh-chip-text">
              <div className="hh-chip-title">95% cheaper</div>
              <div className="hh-chip-sub">vs Twilio aggregators</div>
            </div>
          </div>

          <div className="hh-chip hh-chip--tr" style={{ '--d': '1200ms' }}>
            <span className="hh-chip-icon hh-chip-icon--purple">
              <span className="hh-chip-pulse" />
            </span>
            <div className="hh-chip-text">
              <div className="hh-chip-title">1,247 sent today</div>
              <div className="hh-chip-sub">All through your SIM</div>
            </div>
          </div>

          <div className="hh-chip hh-chip--br" style={{ '--d': '1300ms' }}>
            <span className="hh-chip-icon hh-chip-icon--dark">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </span>
            <div className="hh-chip-text">
              <div className="hh-chip-title">GHL connected</div>
              <div className="hh-chip-sub">Synced in real time</div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .hh {
          background: #ffffff;
          padding: clamp(7rem, 18vh, 13rem) clamp(1rem, 2.5vw, 2rem) 0;
          font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #000;
          -webkit-font-smoothing: antialiased;
          overflow: hidden;
        }

        .hh-container {
          max-width: 820px;
          margin: 0 auto;
          text-align: center;
          padding-bottom: clamp(4rem, 8vh, 6rem);
        }

        /* --- Entrance animation --- */
        .hh-anim {
          opacity: 0;
          transform: translateY(16px);
          animation: hh-rise 720ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
          animation-delay: var(--d, 0ms);
        }
        .hh-anim-panel {
          transform: translateY(40px);
          animation-duration: 900ms;
        }
        @keyframes hh-rise {
          to { opacity: 1; transform: translateY(0); }
        }

        /* --- Subtle continuous float on the dashboard --- */
        .hh-float {
          animation: hh-drift 7s ease-in-out 900ms infinite;
          will-change: transform;
        }
        @keyframes hh-drift {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }

        /* --- Trust row --- */
        .hh-trust {
          display: inline-flex;
          align-items: center;
          gap: 0.625rem;
          margin-bottom: clamp(2rem, 4.5vh, 2.75rem);
          font-size: 0.9375rem;
          color: rgba(0,0,0,0.7);
          font-weight: 400;
          letter-spacing: -0.005em;
        }
        .hh-trust strong { color: #000; font-weight: 600; }
        .hh-stars {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          padding-right: 0.25rem;
        }
        .hh-trust-sep {
          width: 1px;
          height: 13px;
          background: rgba(0,0,0,0.14);
          display: inline-block;
        }

        /* --- Headline --- */
        .hh-headline {
          font-size: clamp(2.625rem, 7.5vw, 4.75rem);
          line-height: 1.02;
          font-weight: 600;
          letter-spacing: -0.045em;
          color: #000;
          margin: 0 0 clamp(1.75rem, 3.5vh, 2.5rem);
        }

        /* --- Subhead --- */
        .hh-subhead {
          font-size: clamp(1.0625rem, 1.7vw, 1.1875rem);
          line-height: 1.55;
          color: rgba(0,0,0,0.6);
          font-weight: 400;
          margin: 0 auto clamp(1.75rem, 4vh, 2.75rem);
          max-width: 580px;
          letter-spacing: -0.005em;
        }

        /* --- Benefit row.
           Inline-text reassurances (not chips), separated by a dot. The "No"
           is set in the brand violet so the eye lands on the negative
           immediately — no icon needed. Lighter than the price/CTA row
           visually so it doesn't compete for attention. */
        .hh-benefits {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          row-gap: 0.5rem;
          column-gap: 0.75rem;
          margin: 0 auto clamp(1.5rem, 3.5vh, 2rem);
          max-width: 720px;
          font-size: 0.8125rem;
          letter-spacing: -0.005em;
        }
        .hh-benefit-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          color: rgba(0,0,0,0.72);
          font-weight: 500;
          white-space: nowrap;
        }
        .hh-benefit-no {
          display: inline-flex;
          align-items: center;
          padding: 0.1rem 0.45rem;
          font-size: 0.6875rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          color: #6d28d9;
          background: #ede9fe;
          border-radius: 999px;
          line-height: 1.35;
        }
        .hh-benefit-dot {
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: rgba(0,0,0,0.22);
          flex-shrink: 0;
        }

        /* --- Friction-killer whisper under the CTA.
           Centered, secondary tone, no background — plain text so it reads as
           a passing reassurance, not another visual element competing with
           the button above. */
        .hh-friction {
          margin: -0.25rem 0 clamp(0.75rem, 2vh, 1rem);
          font-size: 0.75rem;
          color: rgba(0,0,0,0.45);
          font-weight: 500;
          letter-spacing: 0.005em;
        }

        /* --- Price + CTA row ---
           One flex line so the price pill sits next to the primary CTA. Wraps
           on small screens. The .hh-price and .hh-ctas children stay sized to
           their content (no width:max-content needed — the row handles
           centering). */
        .hh-price-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.875rem;
          flex-wrap: wrap;
          margin: 0 auto clamp(1.5rem, 3.5vh, 2.25rem);
        }

        /* --- Price callout --- */
        .hh-price {
          display: inline-flex;
          align-items: center;
          gap: 0.625rem;
          background: #ffffff;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 999px;
          padding: 0.5rem 0.625rem 0.5rem 1rem;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -12px rgba(124, 58, 237, 0.22);
          font-size: 0.9375rem;
          flex-wrap: wrap;
          justify-content: center;
        }
        .hh-price-old {
          color: rgba(0,0,0,0.5);
          font-weight: 400;
        }
        .hh-price-old s {
          text-decoration: line-through;
          text-decoration-color: rgba(0,0,0,0.35);
          margin-left: 0.25rem;
        }
        .hh-price-arrow {
          color: rgba(0,0,0,0.35);
          font-weight: 500;
        }
        .hh-price-new {
          display: inline-flex;
          align-items: baseline;
          gap: 0.25rem;
          color: #000;
          font-weight: 600;
          letter-spacing: -0.01em;
        }
        .hh-price-amt {
          font-size: 1rem;
          font-weight: 700;
          background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .hh-price-per { font-weight: 500; color: rgba(0,0,0,0.7); }
        .hh-price-badge {
          display: inline-flex;
          align-items: center;
          background: #ede9fe;
          color: #6d28d9;
          border-radius: 999px;
          padding: 0.25rem 0.625rem;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0;
          margin-left: 0.125rem;
        }

        /* --- CTAs --- */
        .hh-ctas {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
        }
        .hh-btn-primary {
          background: #000;
          color: #fff;
          border: none;
          border-radius: 12px;
          padding: 1rem 2rem;
          font-size: 1rem;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          transition: background 160ms ease, transform 120ms ease, box-shadow 160ms ease;
          letter-spacing: -0.005em;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05), 0 12px 28px -12px rgba(124, 58, 237, 0.45);
        }
        .hh-btn-primary:hover {
          background: #1a1a1a;
          transform: translateY(-1px);
          box-shadow: 0 1px 2px rgba(0,0,0,0.05), 0 16px 32px -12px rgba(124, 58, 237, 0.55);
        }
        .hh-btn-secondary {
          background: transparent;
          color: #000;
          border: 1px solid rgba(0,0,0,0.12);
          border-radius: 10px;
          padding: 0.8125rem 1.375rem;
          font-size: 0.9375rem;
          font-weight: 500;
          font-family: inherit;
          cursor: pointer;
          transition: background 140ms ease, border-color 140ms ease;
          letter-spacing: -0.005em;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        .hh-btn-secondary:hover {
          background: #fafafa;
          border-color: rgba(0,0,0,0.22);
        }
        .hh-play { color: rgba(0,0,0,0.55); }

        /* --- Requirement annotation under CTAs.
           Small, secondary, but with an Android icon so the requirement is
           impossible to miss without overpowering the headline. */
        /* --- Platforms --- */
        .hh-platforms {
          font-size: 0.875rem;
          color: rgba(0,0,0,0.5);
          font-weight: 400;
          margin: 0;
          letter-spacing: -0.005em;
        }
        .hh-platforms a {
          color: rgba(0,0,0,0.78);
          text-decoration: underline;
          text-decoration-color: rgba(0,0,0,0.2);
          text-underline-offset: 3px;
          text-decoration-thickness: 1px;
          transition: text-decoration-color 140ms ease, color 140ms ease;
        }
        .hh-platforms a:hover {
          color: #000;
          text-decoration-color: #000;
        }

        /* Emphasized GHL chip — pill with Play Store logo inline */
        .hh-platforms-ghl {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          background: #ede9fe;
          color: #5b21b6 !important;
          padding: 2px 8px 2px 4px;
          border-radius: 999px;
          text-decoration: none !important;
          font-weight: 500;
          vertical-align: middle;
          transition: background 140ms ease, color 140ms ease, transform 140ms ease;
          line-height: 1.2;
        }
        .hh-platforms-ghl:hover {
          background: #ddd6fe;
          color: #4c1d95 !important;
          transform: translateY(-1px);
          text-decoration: none !important;
        }
        .hh-platforms-ghl-logo {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          object-fit: cover;
          display: inline-block;
          box-shadow: 0 1px 3px rgba(124, 58, 237, 0.2);
        }

        /* --- Product panel — near full-width --- */
        .hh-product-panel {
          max-width: 1680px;
          margin: 0 auto;
          background: #ede9fe;
          border-radius: clamp(18px, 2vw, 28px);
          padding: clamp(2rem, 5vw, 5rem) clamp(1rem, 3vw, 3rem) 0;
          position: relative;
          overflow: hidden;
        }
        .hh-product-panel::before {
          content: "";
          position: absolute;
          inset: -25% -10% auto -10%;
          height: 60%;
          background: radial-gradient(ellipse at center, rgba(167, 139, 250, 0.42), transparent 60%);
          pointer-events: none;
          animation: hh-glow 8s ease-in-out infinite;
        }
        @keyframes hh-glow {
          0%, 100% { opacity: 1;   transform: translateY(0); }
          50%      { opacity: 0.75; transform: translateY(12px); }
        }
        .hh-product-frame {
          position: relative;
          border-radius: 14px 14px 0 0;
          box-shadow:
            0 1px 0 rgba(0,0,0,0.04),
            0 28px 56px -14px rgba(76, 29, 149, 0.38),
            0 -1px 0 rgba(255,255,255,0.6) inset;
          background: #fff;
          max-width: 1560px;
          margin: 0 auto;
        }
        .hh-product-image {
          display: block;
          width: 100%;
          height: auto;
          border-radius: 14px 14px 0 0;
        }

        /* --- Floating benefit chips --- */
        .hh-chip {
          position: absolute;
          z-index: 5;
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          background: #ffffff;
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: 16px;
          padding: 0.75rem 1.125rem 0.75rem 0.75rem;
          box-shadow:
            0 1px 2px rgba(0,0,0,0.06),
            0 22px 44px -14px rgba(76, 29, 149, 0.3),
            0 6px 14px -4px rgba(0,0,0,0.1);
          opacity: 0;
          animation:
            hh-chip-in 700ms cubic-bezier(0.16, 1, 0.3, 1) forwards,
            hh-chip-float 6s ease-in-out infinite;
          animation-delay: var(--d, 0ms), calc(var(--d, 0ms) + 700ms);
          will-change: transform;
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
        }
        .hh-chip--tl { top: 8%;  left: 3%;  }
        .hh-chip--ml { top: 42%; left: 1%;  animation-duration: 700ms, 6.5s; }
        .hh-chip--bl { bottom: 14%; left: 5%; animation-duration: 700ms, 7.5s; }
        .hh-chip--tr { top: 14%; right: 3%; animation-duration: 700ms, 7s; }
        .hh-chip--br { bottom: 18%; right: 4%; animation-duration: 700ms, 8s; }

        @keyframes hh-chip-in {
          from { opacity: 0; transform: translateY(14px) scale(0.92); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @keyframes hh-chip-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-8px); }
        }

        .hh-chip-icon {
          width: 34px;
          height: 34px;
          border-radius: 9px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .hh-chip-icon--green   { background: #ecfdf5; color: #047857; }
        .hh-chip-icon--purple  { background: #ede9fe; color: #6d28d9; position: relative; }
        .hh-chip-icon--dark    { background: #0a0a0a; color: #fff; }
        .hh-chip-icon--violet  { background: #f5f3ff; color: #6d28d9; }
        .hh-chip-icon--savings {
          background: linear-gradient(135deg, #ede9fe 0%, #fae8ff 100%);
          color: #6d28d9;
        }

        .hh-chip-pulse {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: #7c3aed;
          position: relative;
        }
        .hh-chip-pulse::after {
          content: "";
          position: absolute;
          inset: -6px;
          border-radius: 50%;
          background: rgba(124, 58, 237, 0.4);
          animation: hh-chip-ping 1.8s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        @keyframes hh-chip-ping {
          0%   { transform: scale(0.6); opacity: 0.8; }
          100% { transform: scale(2);   opacity: 0; }
        }

        .hh-chip-text { line-height: 1.2; }
        .hh-chip-title {
          font-size: 0.9375rem;
          font-weight: 600;
          color: #000;
          letter-spacing: -0.015em;
        }
        .hh-chip-sub {
          font-size: 0.75rem;
          color: rgba(0,0,0,0.55);
          margin-top: 3px;
          letter-spacing: -0.005em;
        }

        @media (max-width: 1100px) {
          .hh-chip--ml, .hh-chip--bl { display: none; }
          .hh-chip--tl { left: 0; top: 8%; }
          .hh-chip--tr { right: 0; top: 18%; }
          .hh-chip--br { right: 2%; bottom: 10%; }
        }
        @media (max-width: 640px) {
          .hh-chip { display: none; }
        }

        @media (prefers-reduced-motion: reduce) {
          .hh-anim, .hh-float, .hh-product-panel::before {
            animation: none;
            opacity: 1;
            transform: none;
          }
        }

        @media (max-width: 640px) {
          .hh { padding-top: 5.5rem; }
          .hh-headline br { display: none; }
          .hh-trust { font-size: 0.875rem; gap: 0.5rem; }
          .hh-price {
            font-size: 0.75rem;
            gap: 0.4rem;
            padding: 0.35rem 0.45rem 0.35rem 0.7rem;
          }
          .hh-price-amt { font-size: 0.875rem; }
          .hh-ctas {
            width: 100%;
            flex-direction: column;
            gap: 0.625rem;
          }
          .hh-btn-primary,
          .hh-btn-secondary {
            width: 100%;
            justify-content: center;
            font-size: 0.9375rem;
            padding: 0.875rem 1rem;
          }
          .hh-product-panel { padding: 1.5rem 0.75rem 0; }
        }
      `}</style>
    </section>
  );
}
